import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  Users,
  ClipboardText,
  Handshake,
  VideoCamera,
  Warning,
  SignOut,
  ArrowRight,
  House,
  Bell,
  Clock,
  CheckCircle,
  Calendar,
  Plus,
  Trash,
  Eye,
  Lightning,
  CaretRight,
  Timer,
  CurrencyDollar,
  UserCircle,
  ChatCircle,
  Sparkle,
  Flag,
  UserGear
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    reminder_type: 'general',
    user_id: ''
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/overview`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const createReminder = async () => {
    try {
      await axios.post(`${API}/admin/reminders`, {
        ...reminderForm,
        user_id: reminderForm.user_id || null
      });
      toast.success('Reminder created');
      setShowReminderDialog(false);
      setReminderForm({ title: '', description: '', due_date: '', priority: 'medium', reminder_type: 'general', user_id: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/admin/reminders/${reminderId}/complete`);
      toast.success('Reminder completed');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/admin/reminders/${reminderId}`);
      toast.success('Reminder deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'not_submitted': 'bg-gray-500',
      'applied': 'bg-amber-500',
      'accepted': 'bg-blue-500',
      'consultation_scheduled': 'bg-purple-500',
      'consultation_done': 'bg-indigo-500',
      'paid': 'bg-emerald-500',
      'profile_complete': 'bg-teal-500',
      'matching': 'bg-pink-500',
      'matched': 'bg-rose-500',
      'paused': 'bg-orange-500',
      'archived': 'bg-stone-500'
    };
    return colors[stage] || 'bg-gray-500';
  };

  const getStageName = (stage) => {
    const names = {
      'not_submitted': 'Not Submitted',
      'applied': 'Applied',
      'accepted': 'Accepted',
      'consultation_scheduled': 'Consult Scheduled',
      'consultation_done': 'Consult Done',
      'paid': 'Paid',
      'profile_complete': 'Profile Complete',
      'matching': 'Matching',
      'matched': 'Matched',
      'paused': 'Paused',
      'archived': 'Archived'
    };
    return names[stage] || stage;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-700';
  };

  const getReminderTypeIcon = (type) => {
    const icons = {
      'follow_up': Bell,
      'consultation': Calendar,
      'payment': CurrencyDollar,
      'match_review': Heart,
      'general': Flag
    };
    const Icon = icons[type] || Flag;
    return <Icon className="w-4 h-4" />;
  };

  const importantStages = ['applied', 'accepted', 'consultation_scheduled', 'consultation_done', 'paid', 'matching', 'matched'];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heart weight="fill" className="w-6 h-6 text-primary" />
              <span className="font-serif text-xl text-white">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-stone-400 text-sm">Welcome, {user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-400 hover:text-white" data-testid="admin-logout-btn">
                <SignOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="py-4 text-white border-b-2 border-primary text-sm font-medium">
              <House className="w-4 h-4 inline mr-2" />Overview
            </Link>
            <Link to="/admin/applications" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <ClipboardText className="w-4 h-4 inline mr-2" />Applications
            </Link>
            <Link to="/admin/database" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <Users className="w-4 h-4 inline mr-2" />User Database
            </Link>
            <Link to="/admin/matching" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <Handshake className="w-4 h-4 inline mr-2" />Matching
            </Link>
            <Link to="/admin/team" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Admin Team
            </Link>
            <Link to="/admin/account" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <UserGear className="w-4 h-4 inline mr-2" />My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header with Quick Stats */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2" data-testid="admin-dashboard-title">
              Matchmaking Control Center
            </h1>
            <p className="text-stone-400">
              {dashboardData?.stats?.total_users || 0} users • {dashboardData?.stats?.active_matches || 0} active matches
            </p>
          </div>
          <Button onClick={() => setShowReminderDialog(true)} className="btn-primary" data-testid="create-reminder-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Reminder
          </Button>
        </div>

        {/* Alert Banner - Overdue Tasks */}
        {dashboardData?.overdue_count > 0 && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-4" data-testid="overdue-alert">
            <Warning weight="fill" className="w-6 h-6 text-red-400" />
            <div className="flex-1">
              <p className="text-red-200 font-medium">You have {dashboardData.overdue_count} overdue task(s)</p>
              <p className="text-red-300/70 text-sm">Review your reminders to stay on top of your matchmaking</p>
            </div>
            <Button variant="outline" size="sm" className="border-red-500/50 text-red-300 hover:bg-red-900/30">
              View All
            </Button>
          </div>
        )}

        {/* Pipeline Stages Overview */}
        <div className="bg-stone-800 rounded-lg p-6 mb-8" data-testid="pipeline-overview">
          <h2 className="font-serif text-xl text-white mb-4 flex items-center gap-2">
            <Sparkle className="w-5 h-5 text-primary" />
            User Pipeline
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {importantStages.map((stage) => (
              <Link key={stage} to={`/admin/database?stage=${stage}`}>
                <div className="bg-stone-700/50 rounded-lg p-4 hover:bg-stone-700 transition-colors cursor-pointer group" data-testid={`stage-${stage}`}>
                  <div className={`w-3 h-3 rounded-full ${getStageColor(stage)} mb-2`} />
                  <div className="text-2xl font-serif text-white mb-1">
                    {dashboardData?.stage_counts?.[stage] || 0}
                  </div>
                  <div className="text-stone-400 text-xs group-hover:text-stone-300">
                    {getStageName(stage)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Reminders & Tasks Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Active Reminders */}
            <div className="bg-stone-800 rounded-lg p-6" data-testid="reminders-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-400" />
                  Active Reminders
                </h2>
                <span className="text-stone-400 text-sm">
                  {dashboardData?.pending_reminders?.length || 0} pending
                </span>
              </div>

              {dashboardData?.pending_reminders?.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>All caught up! No pending reminders.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {dashboardData?.pending_reminders?.map((reminder) => (
                    <div 
                      key={reminder.id} 
                      className={`bg-stone-700/50 rounded-lg p-4 border-l-4 ${
                        reminder.is_overdue ? 'border-red-500' : 
                        reminder.priority === 'high' ? 'border-amber-500' : 
                        'border-stone-600'
                      }`}
                      data-testid={`reminder-${reminder.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            reminder.is_overdue ? 'bg-red-900/50 text-red-400' : 'bg-stone-600 text-stone-300'
                          }`}>
                            {getReminderTypeIcon(reminder.reminder_type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-white font-medium">{reminder.title}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded border ${getPriorityColor(reminder.priority)}`}>
                                {reminder.priority}
                              </span>
                              {reminder.is_overdue && (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-900/50 text-red-300">
                                  OVERDUE
                                </span>
                              )}
                            </div>
                            {reminder.description && (
                              <p className="text-stone-400 text-sm mb-2">{reminder.description}</p>
                            )}
                            {reminder.user && (
                              <Link to={`/admin/user/${reminder.user.id}`} className="inline-flex items-center gap-1 text-pink-400 text-sm hover:text-pink-300">
                                <UserCircle className="w-4 h-4" />
                                {reminder.user.name}
                              </Link>
                            )}
                            {reminder.due_date && (
                              <div className="flex items-center gap-1 text-stone-500 text-xs mt-1">
                                <Clock className="w-3 h-3" />
                                Due: {new Date(reminder.due_date).toLocaleDateString()} at {new Date(reminder.due_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => completeReminder(reminder.id)}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30"
                            data-testid={`complete-reminder-${reminder.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteReminder(reminder.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Users Needing Attention */}
            {dashboardData?.needs_attention?.length > 0 && (
              <div className="bg-stone-800 rounded-lg p-6" data-testid="needs-attention-section">
                <h2 className="font-serif text-xl text-white mb-4 flex items-center gap-2">
                  <Warning className="w-5 h-5 text-amber-400" />
                  Needs Attention
                </h2>
                <p className="text-stone-400 text-sm mb-4">These users have been waiting for over 24 hours</p>
                <div className="space-y-3">
                  {dashboardData.needs_attention.map((user) => (
                    <div key={user.id} className="flex items-center justify-between bg-stone-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-900/30 flex items-center justify-center">
                          <UserCircle className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{user.name}</h3>
                          <p className="text-stone-400 text-sm">{user.email} • {user.location}</p>
                        </div>
                      </div>
                      <Link to={`/admin/user/${user.id}`}>
                        <Button size="sm" className="btn-primary">
                          <Eye className="w-4 h-4 mr-1" /> Review
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Consultations */}
            <div className="bg-stone-800 rounded-lg p-6" data-testid="consultations-section">
              <h2 className="font-serif text-lg text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                Upcoming Consultations
              </h2>
              {dashboardData?.upcoming_consultations?.length === 0 ? (
                <p className="text-stone-500 text-sm text-center py-4">No upcoming consultations</p>
              ) : (
                <div className="space-y-3">
                  {dashboardData?.upcoming_consultations?.map((consultation) => (
                    <div key={consultation.id} className="bg-stone-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-purple-400" />
                        <span className="text-white font-medium text-sm">{consultation.user?.name}</span>
                      </div>
                      <div className="text-stone-400 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(consultation.scheduled_date).toLocaleDateString()} at {new Date(consultation.scheduled_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-stone-800 rounded-lg p-6" data-testid="activity-section">
              <h2 className="font-serif text-lg text-white mb-4 flex items-center gap-2">
                <Timer className="w-5 h-5 text-blue-400" />
                Recent Activity
              </h2>
              {dashboardData?.recent_activity?.length === 0 ? (
                <p className="text-stone-500 text-sm text-center py-4">No recent activity</p>
              ) : (
                <div className="space-y-3">
                  {dashboardData?.recent_activity?.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex items-center gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full ${getStageColor(activity.stage)}`} />
                      <div className="flex-1">
                        <span className="text-white">{activity.user?.name || 'Unknown'}</span>
                        <span className="text-stone-500 mx-1">→</span>
                        <span className="text-stone-300">{getStageName(activity.stage)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-stone-800 rounded-lg p-6">
              <h2 className="font-serif text-lg text-white mb-4 flex items-center gap-2">
                <Lightning className="w-5 h-5 text-primary" />
                Quick Actions
              </h2>
              <div className="space-y-2">
                <Link to="/admin/applications" className="block">
                  <Button variant="outline" className="w-full justify-between border-stone-700 text-stone-300 hover:bg-stone-700">
                    Review Applications
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/admin/matching" className="block">
                  <Button variant="outline" className="w-full justify-between border-stone-700 text-stone-300 hover:bg-stone-700">
                    Create Match
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/admin/database" className="block">
                  <Button variant="outline" className="w-full justify-between border-stone-700 text-stone-300 hover:bg-stone-700">
                    User Database
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Create Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Create Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Title *</label>
              <Input
                placeholder="What needs to be done?"
                value={reminderForm.title}
                onChange={(e) => setReminderForm(prev => ({ ...prev, title: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
                data-testid="reminder-title-input"
              />
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Description</label>
              <Textarea
                placeholder="Additional details..."
                value={reminderForm.description}
                onChange={(e) => setReminderForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-stone-400 text-sm mb-1 block">Due Date</label>
                <Input
                  type="datetime-local"
                  value={reminderForm.due_date}
                  onChange={(e) => setReminderForm(prev => ({ ...prev, due_date: e.target.value }))}
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
              <div>
                <label className="text-stone-400 text-sm mb-1 block">Priority</label>
                <Select value={reminderForm.priority} onValueChange={(v) => setReminderForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Type</label>
              <Select value={reminderForm.reminder_type} onValueChange={(v) => setReminderForm(prev => ({ ...prev, reminder_type: v }))}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Task</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="match_review">Match Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReminderDialog(false)} className="text-stone-400">
              Cancel
            </Button>
            <Button onClick={createReminder} className="btn-primary" disabled={!reminderForm.title} data-testid="save-reminder-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
